export type Breakpoint =
  | 'LtXS'
  | 'XS'
  | 'LtSm'
  | 'Sm'
  | 'SmLtMd'
  | 'LtMd'
  | 'Md'
  | 'MdLtLg'
  | 'LtLg'
  | 'Lg'
  | 'LtXL'
  | 'XL';

let bodyMaxWidth = '1920px';

// This is a workaround to set use full width layout for new design
if (typeof window !== 'undefined' && window.location.pathname.includes('/new-cms')) {
  bodyMaxWidth = 'initial';
}

const _colors = {
  white: '#fff',
  black: '#000',
  blue_light: '#e6f3fb',
  blue: '#0D78C5',
  blue_darken: '#0048b5',
  green: '#13AA13',
  green_hover: '#32b232',
  green_transparent: 'rgba(0,188,0,0.05)',
  light_green: '#b8e5b8',
  brand_green: '#3ac83a',
  shade_green_10: '#e7f7e7',
  red: '#e01e59',
  dark_red: '#e30f21',
  light_red: '#fce7e9',
  sage: '#e3f9e0',
  sage_hover: '#bde8bd',
  orange: '#f24d09',
  light_orange: '#FEEDE6',
  yellow: '#ffdc02',
  light_yellow: '#FFF4C7',
  gigas: '#4548a7',
  jakarta: '#2d2c69',
  calypso: '#2B6188',
  calypso_hover: '#376794',
  calypso_disabled: '#e2e8ed',

  gray20: '#333',
  gray40: '#4d4d4d',
  gray50: '#808080',
  gray65: '#b6b6b6',
  gray80: '#dcdcdc',
  gray85: '#e6e6e6',
  gray92: '#f1f1f1',
  gray95: '#f5f5f5',

  teal95: '#faf9f8',

  dark10: 'rgba(0, 0, 0, 0.1)',
  dark30: 'rgba(0, 0, 0, 0.3)',
  dark50: 'rgba(0, 0, 0, 0.5)',
  dark60: 'rgba(0, 0, 0, 0.6)',

  light20: 'rgba(255, 255, 255, 0.2)',
};

const theme = {
  colors: {
    bg: {
      main: _colors.white,
      accent: _colors.gray95,
      alt: _colors.black,
      outer: _colors.gray92,
      active: _colors.green_transparent,
      highlight: _colors.blue_light,
      warning: _colors.light_red,
      negative: _colors.light_red,
      positive: _colors.light_green,
      member: _colors.light_yellow,
      info: _colors.shade_green_10,
      infoActive: _colors.gray65,
      productListing: _colors.teal95,
      chat: _colors.black,
    },
    text: {
      main: _colors.black,
      alt: _colors.gray40,
      ghost: _colors.gray50,
      link: _colors.blue,
      linkHover: _colors.blue_darken,
      onColor: _colors.white,
      onAlt: _colors.white,
      highlight: _colors.orange,
      warning: _colors.dark_red,
      sale: _colors.orange,
      placeholder: _colors.gray50,
      disabled: _colors.dark60,
    },
    badge: {
      sale: _colors.dark_red,
      highlight: _colors.orange,
    },
    coupon: {
      bg: _colors.orange,
      disabled: {
        default: _colors.gray85,
        text: _colors.dark60,
      },
    },
    button: {
      base: {
        default: _colors.black,
        hover: _colors.gray20,
        text: _colors.white,
      },
      positive: {
        default: _colors.sage,
        hover: _colors.sage_hover,
        text: _colors.green,
      },
      neutral: {
        default: _colors.calypso,
        hover: _colors.calypso_hover,
        text: _colors.white,
        disabled: _colors.calypso_disabled,
      },
      forward: {
        default: _colors.green,
        hover: _colors.green_hover,
      },
      soft: {
        default: _colors.gray95,
        text: _colors.black,
        hover: _colors.gray92,
      },
      warning: {
        default: _colors.light_red,
        text: _colors.dark_red,
        hover: _colors.light_orange,
      },
      ghost: {
        default: _colors.dark10,
        text: _colors.black,
        hover: _colors.gray65,
        disabledBackground: _colors.gray95,
        disabledText: _colors.dark30,
      },
      disabled: {
        default: _colors.gray85,
        text: _colors.dark50,
      },
    },
    toggle: {
      bg: _colors.gray50,
      thumb: _colors.white,
    },
    border: {
      main: _colors.dark10,
      hover: _colors.gray65,
      alt: _colors.gray85,
      onColor: _colors.light20,
      active: _colors.blue,
      activeAlt: _colors.green,
      contrast: _colors.dark30,
    },
    ui: {
      positive: _colors.green,
      negative: _colors.dark_red,
      neutral: _colors.yellow,
      unknown: _colors.gray50,
      info: _colors.blue,
    },
    dots: {
      active: _colors.gray20,
      inactive: _colors.gray80,
    },
    input: {
      disabled: _colors.gray95,
      checkbox: {
        selected: _colors.brand_green,
      },
    },
    datepicker: {
      background: _colors.gigas,
      inputBackground: _colors.jakarta,
    },
    loader: _colors.black,
  },

  skeleton: {
    loader: _colors.gray85,
    backLoader: _colors.gray95,
  },

  voucher: {
    head: {
      bg: _colors.light_orange,
    },
  },
  font: {
    family: {
      main: '"Source Sans Pro", sans-serif',
      brand: '"Bulldog Mio", "Source Sans Pro", sans-serif',
    },
    lineHeight: '1.5',
    letterSpacing: '-0.01em',
    weight: {
      thin: '300',
      regular: '400',
      medium: '600',
      bold: '700',
    },
  },
  layers: {
    panel: 100,
    belowPanel: 99,
    previewDatePicker: 40,
    header: 50,
    headerNav: 90,
    headerNavUiBlock: 89,
    fullScreenGallery: 110,
    cookieConsent: 250,
    backdrop: 100,
    belowBackdrop: 99,
    chat: 260,
  },
  distances: {
    baseUnit: '8px',
    containerMaxWidth: '1404px',
    containerGutter: '16px',
    containerGutterMd: '32px',
    bodyMaxWidth: bodyMaxWidth,
    contentMaxWidth: '800px', // Used to limit width of text content
    inputHeight: '48px',
  },
  screens: {
    XXS: '@media  (min-width: 319px) ',
    LtXS: '@media (max-width: 374px)',
    XS: '@media (min-width: 375px)',
    LtXSP: '@media (max-width: 429px)',
    XSPLtMd: '@media (min-width: 430px) and (max-width: 899px)',
    LtSm: '@media (max-width: 599px)',
    Sm: '@media (min-width: 600px)',
    SmLtMd: '@media (min-width: 600px) and (max-width: 899px)',
    LtMd: '@media (max-width: 899px)',
    Md: '@media (min-width: 900px)',
    MdLtLg: '@media (min-width: 900px) and (max-width: 1199px)',
    LtLg: '@media (max-width: 1199px)',
    Lg: '@media (min-width: 1200px)',
    LtXL: '@media (max-width: 1799px)',
    XL: '@media (min-width: 1800px)',
  },
};

export { theme };
